import type React from 'react'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { useAuth } from '../contexts/AuthContext'
import { theme } from '../theme/theme'

const PageContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) => theme.colors.background};
  padding: ${theme.spacing.lg};
`

const FormContainer = styled.div`
  max-width: 400px;
  width: 100%;
  padding: ${theme.spacing.xl};
  background-color: ${({ theme }) => theme.colors.surface};
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.lg};
`

const LogoContainer = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  gap: ${theme.spacing.sm};
  padding-bottom: ${theme.spacing.xl};
  margin-bottom: ${theme.spacing.xl};
  border-bottom: 1px solid ${({ theme }) => theme.colors.textMuted};
`

const LogoText = styled.h1`
  font-size: ${theme.typography.xl};
  font-family: 'Inter', sans-serif;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text};
  width: 100%;
  text-align: center;
`

const Logo = styled.img`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  object-fit: contain;
  margin: 0 auto;
`

const FormTitle = styled.h1`
  font-size: ${theme.typography.xl};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  text-align: center;
  margin-bottom: ${theme.spacing.lg};
`

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
`

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xs};
`

const FormLabel = styled.label`
  font-size: ${theme.typography.md};
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text};
`

const FormInput = styled.input`
  background-color: ${({ theme }) => theme.colors.background};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${theme.borderRadius.sm};
  padding: ${theme.spacing.md};
  color: ${({ theme }) => theme.colors.text};
  font-size: ${theme.typography.md};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.textMuted};
  }

  &:disabled {
    background-color: ${({ theme }) => theme.colors.surfaceHover};
    opacity: 0.6;
  }
`

const SubmitButton = styled.button<{ $loading?: boolean }>`
  background-color: ${({ theme }) => theme.colors.primary};
  border: none;
  border-radius: ${theme.borderRadius.sm};
  padding: ${theme.spacing.md};
  color: #000000;
  font-size: ${theme.typography.md};
  font-weight: 600;
  cursor: ${(props) => (props.$loading ? 'not-allowed' : 'pointer')};
  transition: ${theme.transitions.default};
  margin-top: ${theme.spacing.sm};

  &:hover {
    background-color: ${({ theme, $loading }) => ($loading ? theme.colors.primary : theme.colors.primaryDark)};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

const ErrorMessage = styled.div`
  background-color: rgba(255, 107, 107, 0.1);
  border: 1px solid #ff6b6b;
  border-radius: ${theme.borderRadius.sm};
  padding: ${theme.spacing.sm};
  color: #ff6b6b;
  font-size: ${theme.typography.md};
  margin-top: ${theme.spacing.sm};
`

const SuccessMessage = styled.div`
  background-color: rgba(151, 251, 87, 0.1);
  border: 1px solid ${({ theme }) => theme.colors.primary};
  border-radius: ${theme.borderRadius.sm};
  padding: ${theme.spacing.md};
  color: ${({ theme }) => theme.colors.primary};
  font-size: ${theme.typography.md};
  margin-top: ${theme.spacing.sm};
  text-align: center;
`

const InfoMessage = styled.div`
  background-color: ${({ theme }) => theme.colors.background};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${theme.borderRadius.sm};
  padding: ${theme.spacing.md};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${theme.typography.sm};
  margin-bottom: ${theme.spacing.md};
  text-align: center;
`

const LinkButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.primary};
  text-decoration: underline;
  cursor: pointer;
  font-size: ${theme.typography.md};
  margin-top: ${theme.spacing.md};

  &:hover {
    color: ${({ theme }) => theme.colors.primaryDark};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

const ResetPasswordPage: React.FC = () => {
  const { isPasswordRecovery, changePassword, clearPasswordRecovery } = useAuth()
  const navigate = useNavigate()
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  useEffect(() => {
    // If not in password recovery mode, redirect to login after a delay
    if (!isPasswordRecovery && !success) {
      const timer = setTimeout(() => {
        navigate('/login')
      }, 10000)
      return () => clearTimeout(timer)
    }
  }, [isPasswordRecovery, success, navigate])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    // Validation
    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters long')
      return
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match')
      return
    }

    setLoading(true)

    try {
      const { error } = await changePassword(newPassword)
      if (error) {
        setError(error.message)
      } else {
        setSuccess(true)
        clearPasswordRecovery()
        // Redirect to home after 3 seconds
        setTimeout(() => {
          navigate('/')
        }, 3000)
      }
    } catch (err) {
      setError('An unexpected error occurred')
    } finally {
      setLoading(false)
    }
  }

  if (!isPasswordRecovery && !success) {
    return (
      <PageContainer>
        <FormContainer>
          <LogoContainer>
            <Logo src="/logo_512.png" alt="supacoach Logo" />
            <LogoText>supacoach</LogoText>
          </LogoContainer>
          <FormTitle>Invalid Link</FormTitle>
          <InfoMessage>
            This password reset link is invalid or has expired. You will be redirected to the login page.
          </InfoMessage>
          <LinkButton onClick={() => navigate('/login')}>Go to Login</LinkButton>
        </FormContainer>
      </PageContainer>
    )
  }

  if (success) {
    return (
      <PageContainer>
        <FormContainer>
          <LogoContainer>
            <Logo src="/logo_512.png" alt="supacoach Logo" />
            <LogoText>supacoach</LogoText>
          </LogoContainer>
          <FormTitle>Password Changed!</FormTitle>
          <SuccessMessage>
            Your password has been successfully updated. You will be redirected to the home page shortly.
          </SuccessMessage>
          <LinkButton onClick={() => navigate('/')}>Go to Home</LinkButton>
        </FormContainer>
      </PageContainer>
    )
  }

  return (
    <PageContainer>
      <FormContainer>
        <LogoContainer>
          <Logo src="/logo_512.png" alt="supacoach Logo" />
          <LogoText>supacoach</LogoText>
        </LogoContainer>
        <FormTitle>Set New Password</FormTitle>
        <InfoMessage>Please enter your new password below.</InfoMessage>
        <Form onSubmit={handleSubmit}>
          <FormField>
            <FormLabel>New Password</FormLabel>
            <FormInput
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter new password"
              required
              minLength={6}
              disabled={loading}
            />
          </FormField>

          <FormField>
            <FormLabel>Confirm Password</FormLabel>
            <FormInput
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
              required
              minLength={6}
              disabled={loading}
            />
          </FormField>

          <SubmitButton type="submit" $loading={loading} disabled={loading}>
            {loading ? 'Updating...' : 'Update Password'}
          </SubmitButton>

          {error && <ErrorMessage>{error}</ErrorMessage>}
        </Form>
      </FormContainer>
    </PageContainer>
  )
}

export default ResetPasswordPage
